/**
 * Types du module gestion-eau — alignés EXACTEMENT sur le schéma Supabase
 * (cf. SUPABASE-SQL.md, snake_case). Les enregistrements locaux Dexie utilisent
 * les mêmes noms de colonnes pour rendre la synchronisation triviale (aucun mapping).
 *
 * Convention :
 *  - `XxxRow`   : forme exacte d'une ligne Supabase (ce qui est envoyé / reçu).
 *  - `XxxLocal` : `XxxRow` + champ Dexie-only `_dirty` (jamais transmis à Supabase).
 *  - timestamps : stockés en ISO string (compatibles timestamptz).
 */

/** Champ technique local — présent uniquement dans Dexie, retiré avant tout upsert. */
export interface LocalMeta {
  /** true = modifié localement, en attente de push vers Supabase. */
  _dirty?: boolean;
}

// ───────────────────────────── eau_compteurs ─────────────────────────────
export type CompteurType = 'villa' | 'golf' | 'commun';

export interface CompteurRow {
  id: string;
  nom: string;
  type: CompteurType;
  proprietaire: string | null;
  zone: string | null;
  ordre: number | null;
  lat: number | null;
  lng: number | null;
  actif: boolean;
  created_at: string | null;
  updated_at: string | null;
}
export type CompteurLocal = CompteurRow & LocalMeta;

// ──────────────────────────── eau_qr_compteur ────────────────────────────
export interface QrCompteurRow {
  id: string;
  compteur_id: string;
  emplacement: string | null;
  code: string;
  actif: boolean;
  created_at: string | null;
}
export type QrCompteurLocal = QrCompteurRow & LocalMeta;

// ────────────────────────── eau_releves_compteur ─────────────────────────
export interface ReleveCompteurRow {
  id: string;
  compteur_id: string;
  index: number;
  rupture_index: boolean;
  aberrant_confirme: boolean;
  timestamp: string;
  agent_id: string | null;
  note: string | null;
  photo_url: string | null;
}
export type ReleveCompteurLocal = ReleveCompteurRow & LocalMeta;

// ─────────────────────────── eau_releves_bassin ──────────────────────────
export interface ReleveBassinRow {
  id: string;
  hauteur_cm: number;
  volume_m3: number;
  timestamp: string;
  agent_id: string | null;
  note: string | null;
}
export type ReleveBassinLocal = ReleveBassinRow & LocalMeta;

// ─────────────────────────── eau_entrees_bassin ──────────────────────────
export interface EntreeBassinRow {
  id: string;
  volume_m3: number;
  timestamp: string;
  agent_id: string | null;
  note: string | null;
}
export type EntreeBassinLocal = EntreeBassinRow & LocalMeta;

// ────────────────────────────── eau_bilans ───────────────────────────────
export interface BilanRow {
  id: string;
  timestamp: string;
  timestamp_prev: string | null;
  stock_prev: number | null;
  entrees_m3: number | null;
  conso_m3: number | null;
  stock_attendu: number | null;
  stock_mesure: number | null;
  ecart_m3: number | null;
  ecart_pct: number | null;
  anomalie: boolean;
  traitee: boolean;
  commentaire: string | null;
  // Évolution « bassin/débit » (additif) : apport mesuré (Q_in×Δt ou override),
  // conso réelle vers le réseau (apport − Δstock), pertes (conso réseau − Σ compteurs)
  // et débit courant utilisé pour le calcul. Nullables → rétrocompatibles.
  apport_m3?: number | null;
  conso_reseau_m3?: number | null;
  pertes_m3?: number | null;
  debit_m3h_utilise?: number | null;
}
export type BilanLocal = BilanRow & LocalMeta;

// ──────────────────────────── eau_debit_tests ────────────────────────────
/**
 * Test de débit des pompes « vanne fermée » : on mesure la montée de niveau
 * (cm) sur une durée (min) pour déduire Q_in (m³/h). Le dernier test = débit
 * courant supposé stable ; `ecart_pct` mémorise l'écart vs le test précédent.
 */
export interface DebitTestRow {
  id: string;
  niveau_debut_cm: number;
  niveau_fin_cm: number;
  duree_min: number;
  debit_m3h: number;
  ecart_pct: number | null;
  timestamp: string;
  agent_id: string | null;
  note: string | null;
  created_at: string | null;
}
export type DebitTestLocal = DebitTestRow & LocalMeta;

// ──────────────────────────── eau_arrets_pompe ────────────────────────────
/**
 * Arrêt de pompe saisi à la main : période pendant laquelle les pompes étaient
 * À L'ARRÊT. Sert (Phase 2) à déduire le temps de marche réel sur un intervalle
 * (temps écoulé − Σ arrêts) pour estimer l'apport « débit × temps de marche ». La
 * saisie se fait au choix par DURÉE (+ date/heure de début) ou par HEURES début/fin ;
 * on range toujours la forme canonique (début, fin) — `duree_min` = fin − début.
 */
export interface ArretPompeRow {
  id: string;
  timestamp_debut: string;
  timestamp_fin: string;
  duree_min: number;
  agent_id: string | null;
  note: string | null;
  created_at: string | null;
}
export type ArretPompeLocal = ArretPompeRow & LocalMeta;

// ──────────────────── eau_elec_releves_compteur (ELEC-CC-02) ────────────────────
/** Origine d'un relevé d'électricité : tournée, carnet papier saisi après coup, photo envoyée par le client. */
export type ElecReleveOrigine = 'terrain' | 'carnet' | 'photo_client';

/**
 * Relevé d'index d'un compteur RTB Énergie (kWh). `compteur_id` pointe vers
 * `eau_elec_compteurs` (ELEC-CC-02, plus vers les compteurs d'eau).
 */
export interface ElecReleveRow {
  id: string;
  compteur_id: string;
  index: number;
  rupture_index: boolean;
  aberrant_confirme: boolean;
  timestamp: string;
  agent_id: string | null;
  note: string | null;
  photo_url: string | null;
  /** Absent sur un vieux cache local = 'terrain'. */
  origine?: ElecReleveOrigine;
  created_at: string | null;
  updated_at?: string | null;
}
export type ElecReleveLocal = ElecReleveRow & LocalMeta;

/** Statut d'un client ou d'un compteur RTB : actif, en pause (pas de facture), arrêté. */
export type ElecStatut = 'actif' | 'pause' | 'arrete';

// ───────────────────────────── eau_elec_reglages ─────────────────────────────
export const ELEC_REGLAGES_ID = 'rtb';

/** Réglages de RTB Énergie (une seule ligne `id = 'rtb'`) : émetteur, règlement, calcul, numérotation. */
export interface ElecReglagesRow {
  id: string;
  raison_sociale: string | null;
  forme_juridique: string | null;
  siege: string | null;
  nif: string | null;
  stat: string | null;
  rcs: string | null;
  telephone: string | null;
  email: string | null;
  banque: string | null;
  titulaire_compte: string | null;
  iban: string | null;
  bic: string | null;
  site: string | null;
  puissance_kwc: number | null;
  contrat_ref: string | null;
  tarif_kwh: number;
  forfait_ht: number;
  tva_pct: number;
  echeance_jours: number;
  devise: string;
  numero_annee: number;
  numero_prochain: number;
  mode_essai: boolean;
  /** Compteur d'essai (ELEC-CC-03) : distinct de l'officiel, jamais mélangé. Absent d'un vieux cache = 50 / 2026. */
  numero_essai_prochain?: number;
  numero_essai_annee?: number;
  created_at: string | null;
  updated_at: string | null;
}
export type ElecReglagesLocal = ElecReglagesRow & LocalMeta;

// ───────────────────────── eau_elec_factures (ELEC-CC-03) ─────────────────────────
/** Statuts d'une facture RTB. `payee` et `abandonnee` : phases suivantes. */
export type ElecFactureStatut = 'brouillon' | 'emise' | 'a_valider' | 'validee' | 'envoyee' | 'payee' | 'abandonnee';

export interface ElecFactureRow {
  id: string;
  client_id: string;
  /** null pour une facture groupée (tous les compteurs du client). */
  bien: string | null;
  /** Mois de consommation `AAAA-MM`. */
  cycle: string;
  statut: ElecFactureStatut;
  essai: boolean;
  numero: string | null;
  numero_seq: number | null;
  periode_debut: string | null;
  periode_fin: string | null;
  date_emission: string | null;
  date_echeance: string | null;
  tarif_kwh: number | null;
  forfait_ht: number | null;
  tva_pct: number | null;
  total_kwh: number | null;
  montant_energie: number | null;
  montant_ht: number | null;
  montant_tva: number | null;
  montant_ttc: number | null;
  client_snapshot: Record<string, unknown> | null;
  emetteur_snapshot: Record<string, unknown> | null;
  validation_date_limite: string | null;
  date_envoi: string | null;
  en_attente_releve: boolean;
  /** Ce qui manque pour une facture en attente (« relevé de clôture manquant pour L7 »). */
  motif_attente: string | null;
  note_interne: string | null;
  /** Client, ou client et bien : une seule facture par clé et par cycle. */
  cle_regroupement: string;
  created_at: string | null;
  updated_at: string | null;
}
export type ElecFactureLocal = ElecFactureRow & LocalMeta;

export interface ElecFactureLigneRow {
  id: string;
  facture_id: string;
  compteur_id: string;
  code_compteur: string | null;
  index_debut: number | null;
  date_index_debut: string | null;
  index_fin: number | null;
  date_index_fin: string | null;
  releve_fin_id: string | null;
  kwh: number | null;
  montant: number | null;
  ordre: number | null;
  created_at: string | null;
  updated_at: string | null;
}
export type ElecFactureLigneLocal = ElecFactureLigneRow & LocalMeta;

export interface ElecValidationRow {
  id: string;
  facture_id: string;
  user_id: string;
  avis: 'bon' | 'remarque';
  texte: string | null;
  /** Réponse de la gestion à une remarque. */
  reponse: string | null;
  levee: boolean;
  created_at: string | null;
  updated_at: string | null;
}
export type ElecValidationLocal = ElecValidationRow & LocalMeta;

// ───────────────────────────── eau_elec_clients ─────────────────────────────
export interface ElecClientRow {
  id: string;
  /** Repère court (ex. « A »), unique, comparé tel quel. */
  code: string;
  nom: string;
  raison_sociale: string | null;
  adresse: string | null;
  nif: string | null;
  stat: string | null;
  email: string | null;
  telephone: string | null;
  /** Plusieurs compteurs sur une seule facture. */
  facture_groupee: boolean;
  hors_domaine: boolean;
  statut: ElecStatut;
  /** Compte 1sakely.org du client (espace client, plus tard). */
  user_id: string | null;
  notes_internes: string | null;
  created_at: string | null;
  updated_at: string | null;
}
export type ElecClientLocal = ElecClientRow & LocalMeta;

// ───────────────────────────── eau_elec_compteurs ─────────────────────────────
export interface ElecCompteurRow {
  id: string;
  /** Libellé du carnet du releveur (`L1`, `V8S`, `L2V10`, `H1`...), unique, comparé tel quel. */
  code: string;
  /** Code du lot (`L1`, `V4`, `V8B`, `Hôtel`...). */
  bien: string;
  client_id: string;
  /** Échelle : nombre de décimales de l'index (0 à 3). */
  decimales: number;
  statut: ElecStatut;
  remplace_compteur_id: string | null;
  date_mise_en_service: string | null;
  date_arret: string | null;
  /** Index de clôture de la dernière facture du tableur : départ du premier cycle tenu dans l'app. */
  index_reprise: number | null;
  date_index_reprise: string | null;
  /** Ordre de tournée. */
  ordre: number | null;
  note: string | null;
  created_at: string | null;
  updated_at: string | null;
}
export type ElecCompteurLocal = ElecCompteurRow & LocalMeta;

// ───────────────────────── eau_elec_couts (Phase 1 élec) ─────────────────────────
/**
 * Coûts d'électricité mensuels de la centrale (mutualisés) : facture JIRAMA (A),
 * gasoil du groupe électrogène (B), production totale en kWh (C). Le prix du kWh
 * (D) en découle : `prix_kwh = (total_jirama + total_gasoil) / total_kwh` quand
 * `total_kwh > 0`, sinon null. Sert de base au calcul du montant élec d'une facture.
 */
export interface ElecCoutRow {
  id: string;
  /** Mois facturé au format `YYYY-MM` (unique). */
  mois: string;
  total_jirama: number;
  total_gasoil: number;
  total_kwh: number;
  prix_kwh: number | null;
  devise: string;
  created_at: string | null;
  updated_at: string | null;
}
export type ElecCoutLocal = ElecCoutRow & LocalMeta;

// ────────────────────────────── eau_factures ─────────────────────────────
export type FactureStatut = 'paye' | 'impaye';

export interface FactureRow {
  id: string;
  numero: string | null;
  compteur_id: string | null;
  periode_start: string | null;
  periode_end: string | null;
  index_debut: number | null;
  index_fin: number | null;
  conso_m3: number | null;
  tarif: number | null;
  montant: number | null;
  devise: string;
  statut: FactureStatut;
  date_echeance: string | null;
  paye_at: string | null;
  relance_count: number;
  generated_at: string | null;
  // Facture combinée eau + électricité (Phase 1 élec) : volet électrique. Tous
  // nullables → rétrocompatibles avec les factures eau existantes.
  index_debut_elec: number | null;
  index_fin_elec: number | null;
  conso_kwh: number | null;
  prix_kwh: number | null;
  montant_elec: number | null;
  /** Mois de référence (`YYYY-MM`) du coût élec appliqué (FK logique vers `eau_elec_couts.mois`). */
  cout_mois: string | null;
  /** Montant total = montant (eau) + montant_elec. */
  montant_total: number | null;
}
export type FactureLocal = FactureRow & LocalMeta;

// ─────────────────────────────── eau_config ──────────────────────────────
export interface ConfigRow {
  id: string; // 'singleton'
  bassin_longueur_m: number | null;
  bassin_largeur_m: number | null;
  bassin_hauteur_max_m: number | null;
  /** Hauteur du flotteur (m) — arrêt des pompes / plafond opérationnel (réf. % remplissage). */
  bassin_hauteur_flotteur_m: number | null;
  /** Hauteur du trop-plein (m) — sécurité, atteinte seulement si flotteurs défaillants. */
  bassin_hauteur_trop_plein_m: number | null;
  /**
   * Bande d'hystérésis du flotteur (cm) : profondeur sous le flotteur à laquelle la
   * pompe redémarre (Phase 3, modèle d'apport « flotteur »). Défaut métier 10 cm.
   * Sert à reconnaître le régime de régulation et à borner l'apport estimé.
   */
  bassin_band_flotteur_cm: number | null;
  /** Écart % max entre deux tests de débit au-delà duquel on alerte « débit instable » (déf. 15). */
  debit_ecart_max_pct: number | null;
  tarif_m3: number | null;
  devise: string;
  seuil_pct: number | null;
  seuil_m3: number | null;
  periode_facturation_jours: number | null;
  seuil_aberrant_facteur: number | null;
  jours_sans_releve_alerte: number | null;
  bassin_seuil_critique_pct: number | null;
  numero_facture_seq: number;
  copro_nom: string | null;
  copro_contact: string | null;
  logo_url: string | null;
  langue: string;
  map_centre_lat: number | null;
  map_centre_lng: number | null;
  map_rayon_km: number | null;
  map_zoom_min: number | null;
  map_zoom_max: number | null;
}
export type ConfigLocal = ConfigRow & LocalMeta;

export const CONFIG_SINGLETON_ID = 'singleton';

// ─────────────────────────────── eau_roles ───────────────────────────────
export interface RoleRow {
  user_id: string;
  admin: boolean;
  releveur: boolean;
  /**
   * Promoteur (Phase 2) : lecture TOTALE de tous les écrans (métier + admin) en
   * lecture seule + réglage des seuils d'alerte via RPC. N'écrit rien d'autre
   * (la RLS Phase 1 est le filet serveur). Cumulable, colonne `eau_roles.promoteur`.
   */
  promoteur: boolean;
  /** Domaine Electricite (ELEC-CC-01) : saisit les releves d'electricite. `releveur` = releveur d'eau. */
  releveur_elec?: boolean;
  /** Emet, valide, encaisse, relance les factures d'electricite. */
  gestion_elec?: boolean;
  /** Consulte le tableau de bord electricite. */
  lecture_elec?: boolean;
  /** Valide les factures d'electricite du cycle (ELEC-CC-03). */
  validation_elec?: boolean;
  updated_at: string | null;
}
export type RoleLocal = RoleRow & LocalMeta;

// ─────────────────────────── eau_comptes_client ──────────────────────────
export interface CompteClientRow {
  id: string;
  nom: string;
  contact: string | null;
  compteur_ids: string[]; // jsonb
  code_enrolement: string;
  code_qr: string;
  user_id: string | null;
  actif: boolean;
  created_by: string | null;
  created_at: string | null;
}
export type CompteClientLocal = CompteClientRow & LocalMeta;

// ────────────────────────── eau_demandes_acces ───────────────────────────
export type DemandeStatut = 'en_attente' | 'validee' | 'refusee';

export interface DemandeAccesRow {
  id: string;
  user_id: string | null;
  email: string | null;
  nom: string | null;
  /** Fiche d'accès enrichie (ÉVO 1) : champs capturés sur la vitrine publique. */
  phone: string | null;
  fonction: string | null;
  message: string | null;
  statut: DemandeStatut;
  roles_attribues: unknown | null; // jsonb
  compteurs_visibles: unknown | null; // jsonb
  traitee_par: string | null;
  traitee_at: string | null;
  created_at: string | null;
}
export type DemandeAccesLocal = DemandeAccesRow & LocalMeta;

// ──────────────────────────── eau_invitations ────────────────────────────
/**
 * Invitation par email (octroi automatique du rôle au 1er login Google).
 * Un admin pré-enregistre l'email + les rôles ; à la connexion de la personne
 * avec cette adresse Google, la RPC `eau_claim_invitation()` attribue les rôles
 * (et crée/active le compte client + compteurs si `role_client`). Voir Phase 1.
 */
export type InvitationStatut = 'en_attente' | 'acceptee' | 'revoquee';

export interface InvitationRow {
  id: string;
  /** Nullable : une invitation WhatsApp par jeton n'a pas l'email d'avance. */
  email: string | null;
  nom: string | null;
  phone: string | null;
  role_admin: boolean;
  role_releveur: boolean;
  role_client: boolean;
  /** Promoteur (Phase 3) : invite un promoteur (lecture totale + seuils). Octroyé au login par les RPC de claim. */
  role_promoteur: boolean;
  /** Rôles électricité (ELEC-CC-02), posés au login par les RPC de claim. Absents sur un vieux cache = false. */
  role_releveur_elec?: boolean;
  role_gestion_elec?: boolean;
  role_lecture_elec?: boolean;
  role_validation_elec?: boolean;
  compteur_ids: string[]; // jsonb
  cible: string | null;
  statut: InvitationStatut;
  invited_by: string | null;
  created_at: string | null;
  accepted_by: string | null;
  accepted_at: string | null;
  /** Jeton unguessable d'enrôlement (canal WhatsApp). null pour le canal email. */
  token: string | null;
  /** Expiration du jeton (ISO). null = pas d'expiration. */
  expires_at: string | null;
  /** Canal d'invitation : 'email' (match sur l'adresse Google) ou 'whatsapp' (match sur le jeton). */
  invite_channel: 'email' | 'whatsapp';
}
export type InvitationLocal = InvitationRow & LocalMeta;

// ─────────────────────────────── eau_scans ───────────────────────────────
export interface ScanRow {
  id: string;
  code: string | null;
  type: 'compteur' | 'client' | null;
  compteur_id: string | null;
  client_id: string | null;
  qr_id: string | null;
  emplacement: string | null;
  user_id: string | null;
  role: string | null;
  resultat: string | null;
  timestamp: string;
}
export type ScanLocal = ScanRow & LocalMeta;

// ────────────────────────────── eau_alertes ──────────────────────────────
export type AlerteType =
  | 'anomalie'
  | 'fuite'
  | 'compteur_non_releve'
  | 'bassin_critique'
  | 'flotteur_defaillant'
  | 'debit_instable';

export interface AlerteRow {
  id: string;
  type: AlerteType | null;
  ref_id: string | null;
  message: string | null;
  niveau: string | null;
  lu: boolean;
  traitee: boolean;
  created_at: string | null;
}
export type AlerteLocal = AlerteRow & LocalMeta;

// ─────────────────────────────── eau_audit ───────────────────────────────
export interface AuditRow {
  id: string;
  user_id: string | null;
  action: string | null;
  entite: string | null;
  entite_id: string | null;
  details: unknown | null; // jsonb
  timestamp: string;
}
export type AuditLocal = AuditRow & LocalMeta;

// ────────────────────────────── eau_annonces ─────────────────────────────
export interface AnnonceRow {
  id: string;
  titre: string | null;
  texte: string | null;
  type: 'promo' | 'evenement' | 'communaute' | null;
  /** Domaine de l'annonce (ELEC-CC-01). Absent sur un vieux cache local = 'eau'. */
  domaine?: EauDomaine;
  actif: boolean;
  date_debut: string | null;
  date_fin: string | null;
  created_by: string | null;
  created_at: string | null;
}
export type AnnonceLocal = AnnonceRow & LocalMeta;

// ─────────────────────────── Rôles applicatifs ───────────────────────────
/**
 * Rôles cumulables du module. `client` est dérivé (un compte client lié au user)
 * et reste inexploité en phase 1. `admin`/`releveur` proviennent de eau_roles.
 */
export type EauRole =
  | 'admin'
  | 'releveur'
  | 'client'
  | 'promoteur'
  | 'releveurElec'
  | 'gestionElec'
  | 'lectureElec'
  | 'validationElec';

/** Domaine du module (ELEC-CC-01) : l'eau et l'electricite ne se melangent jamais. */
export type EauDomaine = 'eau' | 'elec';

export interface EauRoles {
  admin: boolean;
  releveur: boolean;
  client: boolean;
  /** Promoteur (Phase 2) : lecture totale + seuils d'alerte ; aucune autre écriture. */
  promoteur: boolean;
  /** Domaine Electricite (ELEC-CC-01). */
  releveurElec: boolean;
  gestionElec: boolean;
  lectureElec: boolean;
  /** Validation des factures d'électricité (ELEC-CC-03), cumulable. */
  validationElec: boolean;
}

/**
 * Client simulé (Simulation de rôle — Phase 2) : la villa / le compte propriétaire
 * (`eau_comptes_client`) dont l'admin emprunte la vue pour re-filtrer les données.
 * `null` hors simulation propriétaire. Porte le PÉRIMÈTRE DE DONNÉES (`compteurIds`)
 * appliqué en mémoire à la surface propriétaire — aucune requête Supabase ajoutée.
 */
export interface EauSimulatedClient {
  /** id du compte client (`eau_comptes_client.id`). */
  id: string;
  /** Utilisateur rattaché à la villa (peut être null si le compte n'est pas encore activé). */
  userId: string | null;
  /** Libellé lisible affiché dans la marque header et le sélecteur (nom de la villa). */
  label: string;
  /** Compteurs visibles de cette villa = périmètre exact des données affichées. */
  compteurIds: string[];
}
